/**
 * Terminal Command Palette — ⌘K overlay.
 *
 * Lineage: V3 terminalWidget.ts + V1 scaffold app.mjs + cross-surface coherence.
 *
 * Three modes inherited from the V3 terminal:
 *   COMMAND  — slash commands (navigate, agent, offers, canvas, etc.)
 *   AGENT    — free-text → agent chat (Pi routes through capability registry)
 *   SEARCH   — fuzzy search across views and recent objects
 *
 * Surface-aware: the terminal adapts to the surface it's running on.
 * On the browser: full command set. On desktop: focused set.
 * On extension: capture-oriented commands.
 *
 * Allen & Co aesthetic: white paper, black ink, monospace, quiet power.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { getClients, applyMembrane } from "../lib/api-clients";
import { detectSurface, getSurfaceCapabilities, getSurfaceLevels, buildPiEnvelope, type SurfaceType, type SurfaceCapability } from "../lib/surface-context";
import type { ViewKey } from "./AppShell";

interface TerminalCommand {
  cmd: string;
  label: string;
  hint?: string;
  category: "nav" | "agent" | "action" | "system" | "surface";
  capability?: SurfaceCapability;
  action: (args: string) => void | Promise<void>;
}

interface TerminalPaletteProps {
  open: boolean;
  onClose: () => void;
  onNavigate: (view: ViewKey) => void;
  isOperator: boolean;
}

interface OutputLine {
  text: string;
  kind: "cmd" | "res" | "err" | "sys" | "agent";
}

export function TerminalPalette({ open, onClose, onNavigate, isOperator }: TerminalPaletteProps) {
  const [input, setInput] = useState("");
  const [output, setOutput] = useState<OutputLine[]>([]);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<string[]>([]);
  const [historyIdx, setHistoryIdx] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const outputRef = useRef<HTMLDivElement>(null);
  const membraneBypass = isOperator;
  const surface = detectSurface();
  const surfaceCaps = getSurfaceCapabilities();

  // Welcome message on open
  useEffect(() => {
    if (open) {
      setOutput([{
        text: `Terminal ready. Surface: ${surface}. Type a command or ask your agent anything.`,
        kind: "sys" as const,
      }]);
      setInput("");
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Auto-scroll
  useEffect(() => {
    if (outputRef.current) outputRef.current.scrollTop = outputRef.current.scrollHeight;
  }, [output]);

  // Global ⌘K / Ctrl+K handler is in AppShell; Escape closes here
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Escape") { e.preventDefault(); onClose(); return; }

    // History navigation
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (history.length === 0) return;
      const newIdx = historyIdx < 0 ? history.length - 1 : Math.max(0, historyIdx - 1);
      setHistoryIdx(newIdx);
      setInput(history[newIdx]);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (historyIdx < 0) return;
      const newIdx = historyIdx + 1;
      if (newIdx >= history.length) { setHistoryIdx(-1); setInput(""); }
      else { setHistoryIdx(newIdx); setInput(history[newIdx]); }
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      handleCommand();
    }
  }, [input, history, historyIdx]);

  const addLine = useCallback((text: string, kind: OutputLine["kind"] = "res") => {
    setOutput(prev => [...prev, { text, kind }]);
  }, []);

  const commands: TerminalCommand[] = [
    // Navigation
    { cmd: "/dashboard", label: "Your Spec", category: "nav", capability: "dashboard", action: () => onNavigate("dashboard") },
    { cmd: "/work", label: "Work Packet", category: "nav", action: () => onNavigate("work-packet") },
    { cmd: "/agent", label: "Agent Chat", category: "nav", capability: "chat", action: () => onNavigate("agent-chat") },
    { cmd: "/offers", label: "Offers", category: "nav", capability: "offers", action: () => onNavigate("offers") },
    { cmd: "/campaigns", label: "Campaigns", category: "nav", capability: "campaigns", action: () => onNavigate("campaigns") },
    { cmd: "/highlights", label: "Highlights", category: "nav", action: () => onNavigate("highlights") },
    { cmd: "/sequences", label: "Sequences", category: "nav", action: () => onNavigate("sequences") },
    { cmd: "/contacts", label: "Contacts", category: "nav", action: () => onNavigate("contacts") },
    { cmd: "/integrations", label: "Integrations", category: "nav", action: () => onNavigate("integrations") },
    { cmd: "/surfaces", label: "Surfaces", category: "nav", action: () => onNavigate("surfaces") },
    { cmd: "/register", label: "Register", category: "nav", capability: "register", action: () => onNavigate("register") },
    { cmd: "/inbox", label: "Inbox", category: "nav", action: () => onNavigate("inbox") },
    { cmd: "/conveyance", label: "Conveyance", category: "nav", capability: "conveyance", action: () => onNavigate("conveyance") },
    { cmd: "/profile", label: "Profile", category: "nav", capability: "profiles", action: () => onNavigate("profile") },
    { cmd: "/community", label: "Community", category: "nav", capability: "community", action: () => onNavigate("community") },
    { cmd: "/settings", label: "Settings", category: "nav", capability: "settings", action: () => onNavigate("settings") },
    // System
    { cmd: "/help", label: "Show available commands", category: "system", action: () => {
      addLine("Commands:", "sys");
      commands.filter(c => c.category !== "agent").forEach(c => {
        addLine(`  ${c.cmd.padEnd(16)} ${c.label}`, "res");
      });
      addLine("", "res");
      addLine("  Or type anything to ask your agent.", "sys");
      addLine(`  Surface: ${surface} | Capabilities: ${surfaceCaps.join(", ")}`, "sys");
    }},
    { cmd: "/whoami", label: "Show session info", category: "system", action: () => {
      addLine(`Identity: ${isOperator ? "operator" : "client"} | Auth: portal JWT`, "sys");
      addLine(`Membrane: ${membraneBypass ? "bypassed (operator)" : "active (client-safe)"}`, "sys");
      addLine(`Surface: ${surface} | Capabilities: ${surfaceCaps.join(", ")}`, "sys");
      addLine(`Workflow levels: ${getSurfaceLevels().map(l => l.level).join(", ")}`, "sys");
    }},
    { cmd: "/surface", label: "Show current surface info", category: "surface", action: () => {
      addLine(`Surface: ${surface}`, "sys");
      addLine(`Capabilities: ${surfaceCaps.join(", ")}`, "sys");
      addLine(`Workflow levels:`, "sys");
      getSurfaceLevels().forEach(l => {
        addLine(`  ${l.level}. ${applyMembrane(l.name, membraneBypass)} — ${applyMembrane(l.description, membraneBypass)}`, "res");
      });
    }},
    { cmd: "/clear", label: "Clear terminal", category: "system", action: () => setOutput([]) },
  ];

  // Add operator-only commands
  if (isOperator) {
    commands.push(
      { cmd: "/fleet", label: "Fleet Overview", category: "nav", capability: "fleet", action: () => onNavigate("operator-fleet") },
      { cmd: "/cross-client", label: "Cross-Client Campaigns", category: "nav", action: () => onNavigate("operator-campaigns") },
    );
  }

  // Filter commands by surface capability
  const visibleCommands = commands.filter(c => {
    if (!c.capability) return true;
    return surfaceCaps.includes(c.capability);
  });

  const handleCommand = useCallback(async () => {
    const raw = input.trim();
    if (!raw) return;

    // Add to history
    setHistory(prev => [...prev, raw].slice(-50));
    setHistoryIdx(-1);
    addLine(`$ ${raw}`, "cmd");
    setInput("");

    // Slash command?
    if (raw.startsWith("/")) {
      const parts = raw.split(/\s+/);
      const verb = parts[0].toLowerCase();
      const args = parts.slice(1).join(" ");

      const cmd = visibleCommands.find(c => c.cmd === verb);
      if (cmd) {
        await cmd.action(args);
        if (cmd.category === "nav") onClose();
        return;
      }

      // Unknown slash command
      addLine(`Unknown command: ${verb}. Type /help for available commands.`, "err");
      return;
    }

    // Free text → agent chat (surface-aware Pi routing)
    setBusy(true);
    try {
      const clients = getClients();
      if (!clients) {
        addLine("Not authenticated. Sign in to talk to your agent.", "err");
        return;
      }
      // Build a Pi envelope with surface context
      const envelope = buildPiEnvelope(raw);
      const res: any = await clients.control.postChat(envelope.message);
      const reply = applyMembrane(res.reply || res.response?.content || "Done.", membraneBypass);
      addLine(reply, "agent");
      if (res.receipt_id) {
        addLine(`Receipt: ${res.receipt_id} | Capability: ${res.capability_used || "default"} | Level: ${res.level || "—"}`, "sys");
      }
    } catch (err: any) {
      addLine(err.message || "Agent unavailable.", "err");
    } finally {
      setBusy(false);
    }
  }, [input, visibleCommands, addLine, onClose, onNavigate, membraneBypass, surface]);

  if (!open) return null;

  // Filter commands for autocomplete
  const matched = input.startsWith("/")
    ? visibleCommands.filter(c => c.cmd.startsWith(input.toLowerCase()) && c.cmd !== input.toLowerCase())
    : [];

  return (
    <div className="terminal-overlay" onClick={onClose}>
      <div className="terminal-panel" onClick={e => e.stopPropagation()}>
        <div className="terminal-header">
          <span className="terminal-badge">TERMINAL</span>
          <span className="terminal-title">command rail · {surface}</span>
          <button className="terminal-close" onClick={onClose}>✕</button>
        </div>
        <div className="terminal-output" ref={outputRef}>
          {output.map((line, i) => (
            <div key={i} className={`term-line ${line.kind}`}>
              {line.text || "\u00A0"}
            </div>
          ))}
          {busy && (
            <div className="term-line agent">
              <span className="term-typing">●●●</span>
            </div>
          )}
        </div>
        {matched.length > 0 && (
          <div className="terminal-suggestions">
            {matched.slice(0, 5).map(c => (
              <button
                key={c.cmd}
                className="term-suggestion"
                onClick={() => { setInput(c.cmd + " "); inputRef.current?.focus(); }}
              >
                <span className="term-sug-cmd mono">{c.cmd}</span>
                <span className="term-sug-label muted">{c.label}</span>
              </button>
            ))}
          </div>
        )}
        <div className="terminal-input-area">
          <span className="terminal-prompt">$</span>
          <input
            ref={inputRef}
            type="text"
            className="terminal-input"
            placeholder="type a command or ask your agent..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      </div>
    </div>
  );
}
